/**
 * Trash feed — buildings and designs the user deleted recently enough that the
 * API will still take them back.
 *
 * Everything about the deadline is server-computed: the window lives in API
 * config and the same number gates the restore endpoints, so the client only
 * prints what it is given. See apps/api/app/Http/Controllers/Api/V1/TrashController.php
 */

/** Which restore endpoint a row belongs to. */
export type TrashItemType = 'trash-project' | 'trash-concept';

export interface TrashItem {
  type: TrashItemType;
  id: string;
  attributes: {
    name: string | null;
    /** Per-building design number; null on buildings, which have none. */
    project_sequence_number: number | null;
    image_url: string | null;
    deleted_at: string | null;
    restorable_until: string | null;
    /** Localized, ready to print. Never recomputed on the client. */
    restore_deadline_label: string;
  };
  relationships: {
    project?: {
      data: { type: string; id: string };
      meta: { name: string | null };
    };
  };
}

export interface TrashResponse {
  data: TrashItem[];
  links: {
    first: string | null;
    last: string | null;
    prev: string | null;
    next: string | null;
  };
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    deletion_window_days: number;
    /** Server-composed explanation of the window, shown in the section banner. */
    notice: string;
  };
}

export type TrashSubtypeFilter = 'all' | 'project' | 'concept';

export function isTrashProject(item: TrashItem): boolean {
  return item.type === 'trash-project';
}

/** Buildings restore by their own id; designs need their building's id too. */
export function trashItemProjectId(item: TrashItem): string | undefined {
  return isTrashProject(item)
    ? item.id
    : item.relationships.project?.data.id;
}
